<script setup>
import { computed, h, onMounted, onUnmounted, ref, watch } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import wootConstants from 'dashboard/constants/globals';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useImpersonation } from 'dashboard/composables/useImpersonation';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSection,
  DropdownItem,
} from 'next/dropdown-menu/base';
import Icon from 'next/icon/Icon.vue';
import Button from 'next/button/Button.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';

const { t } = useI18n();
const store = useStore();
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const currentAccountId = useMapGetter('getCurrentAccountId');
const currentUserAutoOffline = useMapGetter('getCurrentUserAutoOffline');
const currentUserPerformancePaused = useMapGetter(
  'getCurrentUserPerformancePaused'
);
const currentUserManagerAutoAssignment = useMapGetter(
  'getCurrentUserManagerAutoAssignment'
);
const currentAccount = useMapGetter('getCurrentAccount');
const isUpdatingPerformancePause = ref(false);
const isUpdatingManagerAssignment = ref(false);
const elapsedPauseSeconds = ref(0);
let pauseDurationTimer;

const { isImpersonating } = useImpersonation();

const { AVAILABILITY_STATUS_KEYS } = wootConstants;
const statusList = computed(() => {
  return [
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
  ];
});

const statusColors = ['bg-n-teal-9', 'bg-n-amber-9', 'bg-n-slate-9'];

const availabilityStatuses = computed(() => {
  return statusList.value.map((statusLabel, index) => ({
    label: statusLabel,
    value: AVAILABILITY_STATUS_KEYS[index],
    color: statusColors[index],
    icon: h('span', { class: [statusColors[index], 'size-[12px] rounded'] }),
    active: currentUserAvailability.value === AVAILABILITY_STATUS_KEYS[index],
  }));
});

const activeStatus = computed(() => {
  return availabilityStatuses.value.find(status => status.active);
});

const isBusinessManager = computed(() =>
  ['administrator', 'supervisor'].includes(currentAccount.value.role)
);

const managerAssignmentModes = computed(() => [
  {
    value: 'disabled',
    label: t('SIDEBAR.MANAGER_AUTO_ASSIGNMENT.MODES.DISABLED'),
    active: currentUserManagerAutoAssignment.value === 'disabled',
  },
  {
    value: 'backup',
    label: t('SIDEBAR.MANAGER_AUTO_ASSIGNMENT.MODES.BACKUP'),
    active: currentUserManagerAutoAssignment.value === 'backup',
  },
  {
    value: 'standard',
    label: t('SIDEBAR.MANAGER_AUTO_ASSIGNMENT.MODES.STANDARD'),
    active: currentUserManagerAutoAssignment.value === 'standard',
  },
]);

const activeManagerAssignmentMode = computed(
  () => managerAssignmentModes.value.find(mode => mode.active)?.label
);

const autoOfflineToggle = computed({
  get: () => currentUserAutoOffline.value,
  set: autoOffline => {
    store.dispatch('updateAutoOffline', {
      accountId: currentAccountId.value,
      autoOffline,
    });
  },
});

function changeAvailabilityStatus(availability) {
  if (isImpersonating.value) {
    useAlert(t('PROFILE_SETTINGS.FORM.AVAILABILITY.IMPERSONATING_ERROR'));
    return;
  }
  try {
    store.dispatch('updateAvailability', {
      availability,
      account_id: currentAccountId.value,
    });
  } catch (error) {
    useAlert(t('PROFILE_SETTINGS.FORM.AVAILABILITY.SET_AVAILABILITY_ERROR'));
  }
}

const pauseDuration = computed(() => {
  const totalMinutes = Math.max(Math.floor(elapsedPauseSeconds.value / 60), 0);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0) {
    return t('SIDEBAR.PERFORMANCE_PAUSE.DURATION_HOURS_MINUTES', {
      hours,
      minutes,
    });
  }

  return t('SIDEBAR.PERFORMANCE_PAUSE.DURATION_MINUTES', {
    minutes,
  });
});

function updatePauseDuration() {
  const pausedAt = currentAccount.value.performance_paused_at;
  if (!currentUserPerformancePaused.value || !pausedAt) {
    elapsedPauseSeconds.value = 0;
    return;
  }

  elapsedPauseSeconds.value = Math.max(
    Math.floor((Date.now() - new Date(pausedAt).getTime()) / 1000),
    0
  );
}

function startPauseDurationTimer() {
  window.clearInterval(pauseDurationTimer);
  updatePauseDuration();
  pauseDurationTimer = window.setInterval(updatePauseDuration, 30_000);
}

watch(
  () => [
    currentUserPerformancePaused.value,
    currentAccount.value.performance_paused_at,
  ],
  startPauseDurationTimer
);

onMounted(startPauseDurationTimer);
onUnmounted(() => window.clearInterval(pauseDurationTimer));

async function updatePerformancePause(paused) {
  if (isUpdatingPerformancePause.value) return;

  if (isImpersonating.value) {
    useAlert(t('PROFILE_SETTINGS.FORM.AVAILABILITY.IMPERSONATING_ERROR'));
    return;
  }

  isUpdatingPerformancePause.value = true;
  try {
    await store.dispatch('updatePerformancePause', {
      accountId: currentAccountId.value,
      paused,
    });
    useAlert(
      paused
        ? t('SIDEBAR.PERFORMANCE_PAUSE.PAUSED_SUCCESS')
        : t('SIDEBAR.PERFORMANCE_PAUSE.RESUMED_SUCCESS')
    );
  } catch (error) {
    useAlert(t('SIDEBAR.PERFORMANCE_PAUSE.ERROR'));
  } finally {
    isUpdatingPerformancePause.value = false;
  }
}

const performancePauseToggle = computed({
  get: () => currentUserPerformancePaused.value,
  set: paused => updatePerformancePause(paused),
});

async function changeManagerAutoAssignment(mode) {
  if (isUpdatingManagerAssignment.value) return;

  if (isImpersonating.value) {
    useAlert(t('PROFILE_SETTINGS.FORM.AVAILABILITY.IMPERSONATING_ERROR'));
    return;
  }

  isUpdatingManagerAssignment.value = true;
  try {
    await store.dispatch('updateManagerAutoAssignment', {
      accountId: currentAccountId.value,
      mode,
    });
    useAlert(t('SIDEBAR.MANAGER_AUTO_ASSIGNMENT.SUCCESS'));
  } catch (error) {
    useAlert(t('SIDEBAR.MANAGER_AUTO_ASSIGNMENT.ERROR'));
  } finally {
    isUpdatingManagerAssignment.value = false;
  }
}
</script>

<template>
  <DropdownSection class="[&>ul]:overflow-visible">
    <div class="grid gap-0">
      <DropdownItem preserve-open class="gap-1">
        <div class="flex-grow flex items-center gap-1 min-w-0">
          {{ $t('SIDEBAR.SET_YOUR_AVAILABILITY') }}
        </div>
        <DropdownContainer class="shrink-0">
          <template #trigger="{ toggle }">
            <Button
              size="sm"
              color="slate"
              variant="faded"
              icon="i-lucide-chevron-down"
              trailing-icon
              @click="toggle"
            >
              <div class="flex gap-1 items-center min-w-0 text-sm">
                <div class="p-1 flex-shrink-0">
                  <div class="size-2 rounded-sm" :class="activeStatus.color" />
                </div>
                <span class="truncate max-w-[7rem]">
                  {{ activeStatus.label }}
                </span>
              </div>
            </Button>
          </template>
          <DropdownBody class="min-w-32 z-20">
            <DropdownItem
              v-for="status in availabilityStatuses"
              :key="status.value"
              :label="status.label"
              :icon="status.icon"
              class="cursor-pointer"
              @click="changeAvailabilityStatus(status.value)"
            />
          </DropdownBody>
        </DropdownContainer>
      </DropdownItem>
      <DropdownItem>
        <div class="flex-grow min-w-0">
          {{ $t('SIDEBAR.SET_AUTO_OFFLINE.TEXT') }}
          <Icon
            v-tooltip.top="$t('SIDEBAR.SET_AUTO_OFFLINE.INFO_SHORT')"
            icon="i-lucide-info"
            class="inline-block align-middle ms-1 size-4 text-n-slate-10"
          />
        </div>
        <ToggleSwitch v-model="autoOfflineToggle" />
      </DropdownItem>
      <DropdownItem v-if="isBusinessManager" preserve-open class="gap-2">
        <Icon icon="i-lucide-route" class="size-4 shrink-0 text-n-slate-11" />
        <div class="flex-grow min-w-0">
          <div class="font-medium">
            {{ $t('SIDEBAR.MANAGER_AUTO_ASSIGNMENT.TITLE') }}
          </div>
          <div class="mt-0.5 text-xs text-n-slate-11">
            {{ $t('SIDEBAR.MANAGER_AUTO_ASSIGNMENT.DESCRIPTION') }}
          </div>
        </div>
        <DropdownContainer class="shrink-0">
          <template #trigger="{ toggle }">
            <Button
              size="sm"
              color="slate"
              variant="faded"
              icon="i-lucide-chevron-down"
              trailing-icon
              :disabled="isUpdatingManagerAssignment"
              @click="toggle"
            >
              {{ activeManagerAssignmentMode }}
            </Button>
          </template>
          <DropdownBody class="min-w-40 z-20">
            <DropdownItem
              v-for="mode in managerAssignmentModes"
              :key="mode.value"
              :label="mode.label"
              class="cursor-pointer"
              @click="changeManagerAutoAssignment(mode.value)"
            />
          </DropdownBody>
        </DropdownContainer>
      </DropdownItem>
      <DropdownItem
        preserve-open
        class="gap-2 transition-colors"
        :class="currentUserPerformancePaused ? 'bg-n-amber-3' : ''"
      >
        <Icon
          icon="i-lucide-coffee"
          class="size-4 shrink-0"
          :class="
            currentUserPerformancePaused ? 'text-n-amber-11' : 'text-n-slate-11'
          "
        />
        <div class="flex-grow min-w-0">
          <div class="flex items-center justify-between gap-3">
            <span
              class="font-medium"
              :class="currentUserPerformancePaused ? 'text-n-amber-12' : ''"
            >
              {{ $t('SIDEBAR.PERFORMANCE_PAUSE.TITLE') }}
            </span>
            <ToggleSwitch
              v-model="performancePauseToggle"
              :disabled="isUpdatingPerformancePause"
            />
          </div>
          <div
            class="mt-0.5 text-xs"
            :class="
              currentUserPerformancePaused
                ? 'text-n-amber-11'
                : 'text-n-slate-11'
            "
          >
            <template v-if="currentUserPerformancePaused">
              {{
                $t('SIDEBAR.PERFORMANCE_PAUSE.ACTIVE_DESCRIPTION', {
                  duration: pauseDuration,
                })
              }}
            </template>
            <template v-else>
              {{ $t('SIDEBAR.PERFORMANCE_PAUSE.INACTIVE_DESCRIPTION') }}
            </template>
          </div>
        </div>
      </DropdownItem>
    </div>
  </DropdownSection>
</template>
